/**
 * ============================================================================
 * useTransitionMutation — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: POST /api/v1/suppliers/{id}/transition — move a supplier through
 *          the lifecycle state machine (PENDING_REQUEST → QUESTIONNAIRE_ISSUED
 *          → AUDIT_SCHEDULED → ACTIVE → RESTRICTED, etc.). One signer per
 *          transition; the server enforces legal arrows.
 * ============================================================================
 */

import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { apiFetch, newIdempotencyKey } from '../../api/client';
import type {
  SupplierDetail,
} from './useSupplierDetailQuery';
import type { SupplierLifecycleStatus } from '../SupplierWorkbench/useSuppliersQuery';

export interface TransitionInput {
  supplierId: string;
  target_status: SupplierLifecycleStatus;
  rationale: string;
  signature_bundle_id: string;
}

export function useTransitionMutation(): UseMutationResult<
  SupplierDetail,
  Error,
  TransitionInput
> {
  return useMutation<SupplierDetail, Error, TransitionInput>({
    mutationFn: ({ supplierId, ...body }) =>
      apiFetch<SupplierDetail>(
        `/api/v1/suppliers/${encodeURIComponent(supplierId)}/transition`,
        {
          method: 'POST',
          body,
          idempotencyKey: newIdempotencyKey(),
        },
      ),
  });
}
